import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  ConfirmationModal,
  Divider,
  Flex,
  SxProps,
  Text,
  TextWithIcon,
} from '@modusign/design-system';
import { ReactNode } from 'react';
import { useQueryClient } from 'react-query';

import { getAuthorityQueryKey } from '@app/hooks/useAuthority';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import josa from '@app/libs/josa';

const RESOURCE_NAME = '외부문서';

interface ExternalDocumentUsageModalProps {
  isOpen: boolean;
  limit: number;
  usage: number;
  onClose: () => void;
}

export function ExternalDocumentUsageModal({
  isOpen,
  limit,
  usage,
  onClose,
}: ExternalDocumentUsageModalProps) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  const { isPermitted } = useMemberPermissions('MANAGE_PAYMENT');

  return (
    <ConfirmationModal
      closeButtonLabel="닫기"
      confirmButtonClassName={isPermitted ? 'tagContactButton' : ''}
      confirmButtonLabel={isPermitted ? '요금제 상담' : '요금제 둘러보기'}
      isOpen={isOpen}
      maxWidth="xs"
      title={`${RESOURCE_NAME} 사용량`}
      onClose={() => {
        queryClient.invalidateQueries(getAuthorityQueryKey(workspaceId));
        onClose();
      }}
      onConfirm={() => {
        if (isPermitted) {
          return;
        }
        window.open(`https://modusign.co.kr/pricing`, '_blank', 'noopener noreferrer');
      }}
    >
      <Flex flexDirection="column" sx={{ gap: 1 }} textAlign="center">
        <Text as="p" fontWeight="medium">
          요금제로 제공되는 {RESOURCE_NAME} 사용량은 최대 {limit.toLocaleString()}개입니다.
        </Text>
      </Flex>
      <Box my={7} width="100%">
        <Flex width="100%">
          <TableCell
            sx={{
              bg: 'tableBackgroundNeutralDark',
              borderWidth: '1px 1px 1px 1px',
              color: 'textNeutralLight',
              fontSize: '13px',
            }}
          >
            등록된 {RESOURCE_NAME}
          </TableCell>
          <TableCell
            sx={{
              bg: 'tableBackgroundNeutralDark',
              borderWidth: '1px 1px 1px 0px',
              color: 'textNeutralLight',
              fontSize: '13px',
            }}
          >
            잔여 사용량
          </TableCell>
        </Flex>
        <Flex width="100%">
          <TableCell
            sx={{
              borderWidth: '0px 1px 1px 1px',
              color: 'textNeutralBase',
            }}
          >
            {usage.toLocaleString()}개
          </TableCell>
          <TableCell
            sx={{
              borderWidth: '0px 1px 1px 0px',
              color: limit === usage ? 'textNegativeBase' : 'textNeutralBase',
            }}
          >
            {(limit - usage).toLocaleString()}개
          </TableCell>
        </Flex>
      </Box>
      <Flex color="textNeutralBase" flexDirection="column" sx={{ gap: 2 }}>
        <TextWithIcon
          as="p"
          fontSize="sm"
          leftIcon={<FontAwesomeIcon color="#848484" icon={faCheck} size="sm" />}
        >
          더 많은 {josa(RESOURCE_NAME, '을/를')} 이용해야 할 경우 요금제 변경이 필요합니다.
        </TextWithIcon>
      </Flex>
      <Divider mt={7} sx={{ borderColor: 'dividerNeutralSlightlyLight' }} />
    </ConfirmationModal>
  );
}

export function ExternalDocumentUsageExceededModal({
  isOpen,
  limit,
  usage,
  onClose,
}: ExternalDocumentUsageModalProps) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  const { isPermitted } = useMemberPermissions('MANAGE_PAYMENT');

  return (
    <ConfirmationModal
      closeButtonLabel="닫기"
      confirmButtonClassName={isPermitted ? 'tagContactButton' : ''}
      confirmButtonLabel={isPermitted ? '요금제 상담' : '요금제 둘러보기'}
      isOpen={isOpen}
      maxWidth="xs"
      title={`${RESOURCE_NAME} 사용량 초과`}
      variant="warning"
      onClose={() => {
        queryClient.invalidateQueries(getAuthorityQueryKey(workspaceId));
        onClose();
      }}
      onConfirm={() => {
        if (isPermitted) {
          return;
        }
        window.open(`https://modusign.co.kr/pricing`, '_blank', 'noopener noreferrer');
      }}
    >
      <Flex flexDirection="column" sx={{ gap: 1 }} textAlign="center">
        <Text as="p" color="textNegativeBase" fontWeight="medium">
          요금제로 제공되는 {RESOURCE_NAME} 사용량은 최대 {limit.toLocaleString()}개입니다.
        </Text>
        <Text as="p">더 이상 {RESOURCE_NAME}을 이용할 수 없습니다.</Text>
      </Flex>
      <Box my={7} width="100%">
        <Flex width="100%">
          <TableCell
            sx={{
              bg: 'tableBackgroundNeutralDark',
              borderWidth: '1px 1px 1px 1px',
              color: 'textNeutralLight',
              fontSize: '13px',
            }}
          >
            등록된 {RESOURCE_NAME}
          </TableCell>
          <TableCell
            sx={{
              bg: 'tableBackgroundNeutralDark',
              borderWidth: '1px 1px 1px 0px',
              color: 'textNeutralLight',
              fontSize: '13px',
            }}
          >
            초과 사용량
          </TableCell>
        </Flex>
        <Flex width="100%">
          <TableCell
            sx={{
              borderWidth: '0px 1px 1px 1px',
              color: 'textNeutralBase',
            }}
          >
            {usage.toLocaleString()}개
          </TableCell>
          <TableCell
            sx={{
              borderWidth: '0px 1px 1px 0px',
              color: 'textNegativeBase',
            }}
          >
            {(usage - limit).toLocaleString()}개
          </TableCell>
        </Flex>
      </Box>
      <Flex color="textNeutralBase" flexDirection="column" sx={{ gap: 2 }}>
        <TextWithIcon
          as="p"
          fontSize="sm"
          leftIcon={<FontAwesomeIcon color="#848484" icon={faCheck} size="sm" />}
        >
          더 많은 {josa(RESOURCE_NAME, '을/를')} 이용해야 할 경우 요금제 변경이 필요합니다.
        </TextWithIcon>
      </Flex>
      <Divider mt={7} sx={{ borderColor: 'dividerNeutralSlightlyLight' }} />
    </ConfirmationModal>
  );
}

interface TableCellProps extends SxProps {
  children: ReactNode;
}

function TableCell({ children, sx }: TableCellProps) {
  return (
    <Box
      borderColor="tableBorderNeutralBase"
      borderStyle="solid"
      flex={1}
      fontWeight="medium"
      p={4}
      sx={sx}
      textAlign="center"
    >
      {children}
    </Box>
  );
}
